import { ReactNode } from 'react'
import cn from 'classnames'
import { motion } from 'framer-motion'
import { Button } from '@forwardflow/ui-v2'
import { ArrowLeftMdIcon, ChevronRightIcon } from '@forwardflow/icons'

/**
 * The pieces every editor step is built from. Same vocabulary as the
 * setup wizard (modules/onboarding/shell/Card): one question per screen,
 * a plain lede, one primary action in a footer that never moves.
 */

export interface IStepFooter {
   onBack?: () => void
   action: ReactNode
   onAction: () => void
   disabled?: boolean
   loading?: boolean
   /** Why the button is disabled, or what happens next. */
   note?: ReactNode
   secondary?: { label: ReactNode; onClick: () => void; testId?: string }
   testId?: string
}

export const StepFrame = ({
   eyebrow,
   title,
   lede,
   right,
   children,
   footer,
   testId,
}: {
   eyebrow?: ReactNode
   title: ReactNode
   lede?: ReactNode
   right?: ReactNode
   children: ReactNode
   footer?: IStepFooter
   testId?: string
}) => (
   <section className="flex flex-col h-full min-h-0" data-testid={testId}>
      <motion.div
         key={String(title)}
         initial={{ opacity: 0, y: 10 }}
         animate={{ opacity: 1, y: 0 }}
         transition={{ duration: 0.2 }}
         className="flex items-start justify-between gap-4 shrink-0 pb-4"
      >
         <div className="min-w-0">
            {eyebrow && <p data-step-eyebrow className="text-white/45 text-xs mb-1">{eyebrow}</p>}
            <h2 className="text-white font-semibold text-[19px] leading-snug">
               {title}
            </h2>
            {lede && (
               <p className="text-[#99A1AF] text-sm mt-1 max-w-[620px]">
                  {lede}
               </p>
            )}
         </div>
         {right && <div className="shrink-0">{right}</div>}
      </motion.div>

      <div
         className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-4"
         data-testid="step-body"
      >
         {children}
      </div>

      {footer && (
         <div
            className="shrink-0 pt-4 mt-2 border-t border-white/[0.06]"
            data-testid="step-footer"
         >
            <div className="flex items-center gap-3">
               {footer.onBack && (
                  <Button
                     variant="secondary"
                     size="middle"
                     className="!h-11 !px-4"
                     onClick={footer.onBack}
                     disabled={footer.loading}
                  >
                     <ArrowLeftMdIcon width={12} /> Back
                  </Button>
               )}
               <Button
                  size="middle"
                  className="!h-11 flex-1"
                  onClick={footer.onAction}
                  disabled={footer.disabled}
                  loading={footer.loading}
                  data-testid={footer.testId}
               >
                  {footer.action} <ChevronRightIcon width={16} />
               </Button>
            </div>
            <div className="flex items-center justify-between gap-3 min-h-[18px] mt-2">
               <p className="text-white/40 text-xs">{footer.note}</p>
               {footer.secondary && (
                  <button
                     type="button"
                     className="text-white/55 hover:text-white text-xs font-medium"
                     onClick={footer.secondary.onClick}
                     data-testid={footer.secondary.testId}
                  >
                     {footer.secondary.label}
                  </button>
               )}
            </div>
         </div>
      )}
   </section>
)

/** A bordered list; rows divide themselves. */
export const ListCard = ({
   children,
   className,
   testId,
   title,
   hint,
   right,
}: {
   children: ReactNode
   className?: string
   testId?: string
   title?: ReactNode
   hint?: ReactNode
   right?: ReactNode
}) => (
   <div
      className={cn(
         'rounded-xl border border-[#3f3e47] bg-white/[0.02] overflow-hidden',
         className
      )}
      data-testid={testId}
   >
      {(title || right) && (
         <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-white/[0.06]">
            <div className="min-w-0">
               {title && (
                  <p className="text-white text-sm font-medium">{title}</p>
               )}
               {hint && <p className="text-white/45 text-xs mt-0.5">{hint}</p>}
            </div>
            {right && <div className="shrink-0">{right}</div>}
         </div>
      )}
      <div className="divide-y divide-white/[0.06]">{children}</div>
   </div>
)

export const ListRow = ({
   icon,
   title,
   sub,
   right,
   onClick,
   selected,
   testId,
   className,
}: {
   icon?: ReactNode
   title: ReactNode
   sub?: ReactNode
   right?: ReactNode
   onClick?: () => void
   selected?: boolean
   testId?: string
   className?: string
}) => {
   const inner = (
      <>
         {icon && (
            <span
               className={cn(
                  'flex shrink-0 items-center justify-center w-8 h-8 rounded-lg',
                  selected
                     ? 'bg-[#9F7BFF]/20 text-[#C9B8FF]'
                     : 'bg-white/[0.05] text-white/60'
               )}
            >
               {icon}
            </span>
         )}
         <span className="flex-1 min-w-0">
            <span className="block text-sm text-white truncate">{title}</span>
            {sub && (
               <span className="block text-xs text-white/45 mt-0.5 truncate">
                  {sub}
               </span>
            )}
         </span>
         {right && (
            <span className="shrink-0 flex items-center gap-2">{right}</span>
         )}
      </>
   )
   const base = cn(
      'flex items-center gap-3 px-4 min-h-[52px] py-2 w-full text-left',
      onClick && 'hover:bg-white/[0.03] transition-colors',
      selected && 'bg-[#9F7BFF]/[0.07]',
      className
   )
   return onClick ? (
      <button
         type="button"
         className={base}
         onClick={onClick}
         data-testid={testId}
         aria-pressed={selected}
      >
         {inner}
      </button>
   ) : (
      <div className={base} data-testid={testId}>
         {inner}
      </div>
   )
}

export const AddRow = ({
   label,
   onClick,
   testId,
   disabled,
}: {
   label: ReactNode
   onClick: () => void
   testId?: string
   disabled?: boolean
}) => (
   <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="w-full flex items-center gap-3 px-4 h-12 text-left text-sm font-medium text-[#A684FF] hover:bg-[#9F7BFF]/[0.07] disabled:opacity-40 disabled:cursor-not-allowed"
   >
      <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#9F7BFF]/15 text-[#C9B8FF] text-base leading-none">
         +
      </span>
      {label}
   </button>
)

export const Pill = ({
   children,
   tone = 'muted',
   testId,
}: {
   children: ReactNode
   tone?: 'ok' | 'warn' | 'muted' | 'purple' | 'danger'
   testId?: string
}) => (
   <span
      data-testid={testId}
      className={cn(
         'inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap',
         tone === 'ok' && 'bg-emerald-500/15 text-emerald-400',
         tone === 'warn' && 'bg-amber-500/15 text-amber-300',
         tone === 'danger' && 'bg-[#C11574]/20 text-[#ff8ac4]',
         tone === 'purple' && 'bg-[#9F7BFF]/20 text-[#C9B8FF]',
         tone === 'muted' && 'bg-white/[0.08] text-white/60'
      )}
   >
      {children}
   </span>
)

/** A quiet inline text button. */
export const TextButton = ({
   children,
   onClick,
   disabled,
   className,
   testId,
   tone = 'purple',
}: {
   children: ReactNode
   onClick: () => void
   disabled?: boolean
   className?: string
   testId?: string
   tone?: 'purple' | 'muted' | 'danger'
}) => (
   <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
         'text-xs font-medium hover:underline shrink-0 disabled:opacity-40 disabled:no-underline',
         tone === 'purple' && 'text-[#A684FF]',
         tone === 'muted' && 'text-white/55 hover:text-white',
         tone === 'danger' && 'text-[#ff8ac4]',
         className
      )}
   >
      {children}
   </button>
)

/** A small labelled block. */
export const Field = ({
   label,
   hint,
   children,
   className,
}: {
   label?: ReactNode
   hint?: ReactNode
   children: ReactNode
   className?: string
}) => (
   <div className={cn('space-y-1.5', className)}>
      {(label || hint) && (
         <div>
            {label && (
               <p className="text-white/90 text-sm font-medium">{label}</p>
            )}
            {hint && <p className="text-white/45 text-xs mt-0.5">{hint}</p>}
         </div>
      )}
      {children}
   </div>
)

/** A selectable tile; radio or checkbox depending on `role`. */
export const Tile = ({
   selected,
   onClick,
   children,
   className,
   testId,
   disabled,
   role = 'radio',
}: {
   selected: boolean
   onClick: () => void
   children: ReactNode
   className?: string
   testId?: string
   disabled?: boolean
   role?: 'radio' | 'checkbox'
}) => (
   <button
      type="button"
      role={role}
      aria-checked={selected}
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      className={cn(
         'text-left rounded-xl border p-3.5 transition-all w-full',
         selected
            ? 'border-[#9F7BFF] bg-[rgba(159,123,255,0.10)]'
            : 'border-white/10 bg-white/[0.02] hover:border-white/25',
         disabled && 'opacity-40 cursor-not-allowed',
         className
      )}
   >
      {children}
   </button>
)

export const RadioDot = ({ on }: { on: boolean }) => (
   <span
      className={cn(
         'flex shrink-0 items-center justify-center w-[18px] h-[18px] rounded-full border-2',
         on ? 'border-[#A684FF]' : 'border-white/25'
      )}
   >
      {on && <span className="w-2 h-2 rounded-full bg-[#A684FF]" />}
   </span>
)

export const CheckBoxDot = ({ on }: { on: boolean }) => (
   <span
      className={cn(
         'flex shrink-0 items-center justify-center w-[18px] h-[18px] rounded-md border-2 text-[11px] text-white',
         on ? 'border-[#A684FF] bg-[#A684FF]' : 'border-white/25'
      )}
   >
      {on && '✓'}
   </span>
)

/** A monogram disc for a person. */
export const Initials = ({
   name,
   size = 28,
}: {
   name: string
   size?: number
}) => {
   const letters = name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join('')
   return (
      <span
         className="inline-flex items-center justify-center rounded-full bg-[#9F7BFF]/25 text-[#E4DAFF] font-semibold shrink-0"
         style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
         aria-hidden
      >
         {letters || '?'}
      </span>
   )
}

export const EmptyNote = ({
   children,
   testId,
}: {
   children: ReactNode
   testId?: string
}) => (
   <p className="px-4 py-4 text-sm text-white/45" data-testid={testId}>
      {children}
   </p>
)

export const Loading = ({ label = 'Loading' }: { label?: string }) => (
   <div className="flex items-center gap-2 text-white/50 text-sm px-1 py-2">
      <span className="w-4 h-4 rounded-full border-2 border-[#A684FF] border-t-transparent animate-spin" />{' '}
      {label}
   </div>
)

/** Native textarea styled like the Mantine inputs the kit wraps. */
export const TextArea = ({
   value,
   onChange,
   placeholder,
   rows = 3,
   label,
   hint,
   testId,
   disabled,
   maxLength,
}: {
   value: string
   onChange: (v: string) => void
   placeholder?: string
   rows?: number
   label?: ReactNode
   hint?: ReactNode
   testId?: string
   disabled?: boolean
   maxLength?: number
}) => (
   <label className="block space-y-1.5">
      {(label || hint) && (
         <span className="block">
            {label && (
               <span className="block text-white/90 text-sm font-medium">
                  {label}
               </span>
            )}
            {hint && (
               <span className="block text-white/45 text-xs mt-0.5">
                  {hint}
               </span>
            )}
         </span>
      )}
      <textarea
         value={value}
         onChange={(e) => onChange(e.currentTarget.value)}
         placeholder={placeholder}
         rows={rows}
         disabled={disabled}
         maxLength={maxLength}
         data-testid={testId}
         className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#9F7BFF] focus:outline-none disabled:opacity-50 resize-y"
      />
   </label>
)
